import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import type { AxiosProgressEvent } from "axios";
import { useCallback, useEffect, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import FilesDropArea from "@entrilia/shared/components/FilesDropArea";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { getFileExtension } from "@entrilia/shared/utilities/fileHelper";
import { formatPercentage } from "@entrilia/shared/utilities/formatters";
import { defined } from "@entrilia/shared/utilities/typeHelper";
import adminApi from "../../../../../api/adminApi";
import type { DataImportFileContainerInfo } from "../../../../../api/types/dataImportTypes";
import usePageLeaveConfirmation from "../../../../../hooks/usePageLeaveConfirmation";
import {
  acceptedFileExtensions,
  addFilesAction,
  changeNameAction,
  finishSavingAction,
  getInitialFileContainerEditState,
  isStateValid,
  startSavingAction,
  updateUploadedFileProgressAction,
} from "./fileContainerEditState";
import FileContainerFilesGrid from "./FileContainerFilesGrid";
import zipUploaderService from "./zipUploaderService";

interface Props {
  mode: "create" | "update";
  editedFileContainerInfo?: DataImportFileContainerInfo;
  onClose: () => void;
  onSaved: (fileContainerId: string) => void;
}

const createFileContainer = withErrorHandling(adminApi.createDataImportFileContainer);
const uploadFileIntoContainer = withErrorHandling(adminApi.uploadFileIntoDataImportFileContainer);

const FileContainerDialog = ({ mode, editedFileContainerInfo, onClose, onSaved }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const { setPromptOnPageLeave } = usePageLeaveConfirmation(false);

  const [state, setState] = useState(() => getInitialFileContainerEditState(editedFileContainerInfo));

  useEffect(() => {
    setPromptOnPageLeave(state.isSaving);
    return () => {
      setPromptOnPageLeave(false);
    };
  }, [state.isSaving, setPromptOnPageLeave]);

  const handleAddFiles = useCallback((files: File[]) => {
    setState(addFilesAction(files));
  }, []);

  const handleFileUploadProgress = useCallback(
    (fileName: string, fileIndex: number) => (e: AxiosProgressEvent) => {
      setState(updateUploadedFileProgressAction(fileName, fileIndex, e.progress ?? 0));
    },
    []
  );

  const handleSave = async () => {
    setState(startSavingAction());

    let fileContainerId: string | undefined = editedFileContainerInfo?.id;

    if (mode === "create") {
      const [newFileContainer, createError] = await createFileContainer({ name: state.name });
      if (createError) {
        logError(createError, "[FileContainerDialog] createFileContainer");
        sendNotificationError(createError.message);
        setState(finishSavingAction(0));
        return;
      }

      fileContainerId = newFileContainer.id;
    }

    for (let i = 0; i < state.files.length; ++i) {
      const file = defined(state.files[i]);
      setState(updateUploadedFileProgressAction(file.name, i, 0));

      const uploadFn =
        getFileExtension(file.name) === ".zip"
          ? zipUploaderService.uploadZipFileIntoDataImportFileContainer
          : uploadFileIntoContainer;

      const [, uploadError] = await uploadFn(defined(fileContainerId), file, handleFileUploadProgress(file.name, i));

      if (uploadError) {
        logError(uploadError, "[FileContainerDialog] uploadFileIntoContainer");
        sendNotificationError(uploadError.message);
        setState(finishSavingAction(0));
        return;
      }
    }

    setState(finishSavingAction(1));

    sendNotification("Files have been uploaded successfully");
    onSaved(defined(fileContainerId));
  };

  return (
    <Dialog open slotProps={{ paper: { sx: { width: "41.5rem" } } }}>
      <DialogTitle noWrap sx={{ mr: 2 }}>
        {mode === "create"
          ? "Add File Container"
          : `Upload Files to '${editedFileContainerInfo?.containerName ?? "File Container"}'`}
      </DialogTitle>
      {!state.isSaving && <DialogCloseButton onClick={onClose} />}
      <DialogContent>
        <Stack spacing={3} sx={{ height: "33rem" }}>
          {mode === "create" && (
            <TextField
              label="Name"
              fullWidth
              value={state.name}
              onChange={(e) => setState(changeNameAction(e.target.value))}
              disabled={state.isSaving}
            />
          )}

          {state.isSaving && (
            <Alert severity="warning">
              Please wait while the files are uploading. Do not close the browser tab or the data you are downloading
              will be lost.
            </Alert>
          )}

          <Stack spacing={1} sx={{ flex: state.files.length > 0 ? 0.38 : 1 }}>
            <FilesDropArea
              allowMultiple
              acceptedFileExtensions={acceptedFileExtensions}
              subtitle="Supported formats: ZIP, PDF, video and MS Office files."
              subtitleDescription="When uploading a large number of files, it is recommended to speed up the process by placing them into a ZIP archive and uploading it as a single file. Please ensure the ZIP archive contains only files and no folders."
              onFilesAdd={handleAddFiles}
              containerProps={{ sx: { flex: 1 } }}
            />
            {state.addFilesError && <FormHelperText error>{state.addFilesError}</FormHelperText>}
          </Stack>

          {state.totalProgress !== undefined && (
            <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
              <LinearProgress variant="determinate" value={state.totalProgress * 100} sx={{ flex: 1 }} />
              <Typography color="textSecondary">{formatPercentage(state.totalProgress)}</Typography>
            </Stack>
          )}

          {state.files.length > 0 && <FileContainerFilesGrid state={state} setState={setState} />}
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose} cancelDisabled={state.isSaving}>
        <Button loading={state.isSaving} onClick={handleSave} variant="contained" disabled={!isStateValid(state)}>
          {mode === "create" ? "Create & Upload" : "Upload"}
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default FileContainerDialog;
